/**
 * 🔒 Scope Service — Hierarchical Data Visibility
 *
 * Separates RBAC (what you can DO) from SCOPE (what you can SEE).
 * Scope is automatic based on role_type in user_organizations.
 *
 * Hierarchy:
 *   director     → sees entire organization
 *   manager      → sees their department
 *   lead         → sees their team (direct reports)
 *   operational  → sees only own tasks
 */

import { supabase } from '../lib/supabaseClient';

// ─── Types ──────────────────────────────────────────────────

export type RoleType = 'operational' | 'lead' | 'manager' | 'director';

export type ScopeType = 'self' | 'team' | 'department' | 'organization';

export interface DataScope {
  type: ScopeType;
  user_id?: string;
  department_id?: string;
  team_user_ids?: string[];
}

export interface UserScopeProfile {
  role_type: RoleType;
  department_id: string | null;
  reports_to_user_id: string | null;
}

// ─── Fetch user scope profile from DB ───────────────────────

export async function fetchUserScopeProfile(
  userId: string,
  organizationId: string,
): Promise<UserScopeProfile> {
  const { data, error } = await supabase
    .from('user_organizations')
    .select('role_type, department_id, reports_to_user_id')
    .eq('user_id', userId)
    .eq('organization_id', organizationId)
    .single();

  if (error || !data) {
    return { role_type: 'operational', department_id: null, reports_to_user_id: null };
  }

  return {
    role_type: (data.role_type as RoleType) ?? 'operational',
    department_id: data.department_id ?? null,
    reports_to_user_id: data.reports_to_user_id ?? null,
  };
}

// ─── Resolve scope from profile ─────────────────────────────

export async function getUserScope(
  userId: string,
  organizationId: string,
  profile?: UserScopeProfile,
): Promise<DataScope> {
  const p = profile ?? await fetchUserScopeProfile(userId, organizationId);

  switch (p.role_type) {
    case 'director':
      return { type: 'organization' };

    case 'manager':
      return {
        type: 'department',
        department_id: p.department_id ?? undefined,
        user_id: userId,
      };

    case 'lead': {
      // Recursive: direct + indirect reports (entire subordinate subtree)
      const teamIds = await getAllSubordinateIds(userId, organizationId);
      return {
        type: 'team',
        user_id: userId,
        team_user_ids: [userId, ...teamIds],
      };
    }

    default:
      return {
        type: 'self',
        user_id: userId,
      };
  }
}

// ─── Get direct report user IDs ─────────────────────────────

async function getDirectReportIds(
  userId: string,
  organizationId: string,
): Promise<string[]> {
  const { data, error } = await supabase
    .from('user_organizations')
    .select('user_id')
    .eq('organization_id', organizationId)
    .eq('reports_to_user_id', userId);

  if (error || !data) return [];
  return data.map(row => row.user_id);
}

// ─── Get ALL subordinate IDs (direct + indirect, recursive BFS) ─

/**
 * Walks the reports_to_user_id graph downward (BFS) and returns every
 * descendant user_id of the given root. Cycle-safe via visited set.
 * Tenant-scoped: only traverses rows of the given organizationId.
 */
export async function getAllSubordinateIds(
  rootUserId: string,
  organizationId: string,
): Promise<string[]> {
  const visited = new Set<string>([rootUserId]);
  const collected: string[] = [];
  let frontier: string[] = [rootUserId];

  // Cap depth to avoid pathological loops in malformed data
  for (let depth = 0; depth < 20 && frontier.length > 0; depth++) {
    const { data, error } = await supabase
      .from('user_organizations')
      .select('user_id, reports_to_user_id')
      .eq('organization_id', organizationId)
      .in('reports_to_user_id', frontier);

    if (error || !data || data.length === 0) break;

    const next: string[] = [];
    for (const row of data) {
      if (!row.user_id || visited.has(row.user_id)) continue;
      visited.add(row.user_id);
      collected.push(row.user_id);
      next.push(row.user_id);
    }
    frontier = next;
  }

  return collected;
}

// ─── Apply scope filter to Supabase query ───────────────────

/**
 * Applies hierarchical scope filter to any Supabase query.
 * The table must have: assigned_to (user), department_id, organization_id.
 *
 * @param query - Supabase query builder
 * @param scope - Resolved DataScope
 * @param userColumn - Column name for user assignment (default: 'assigned_to')
 * @param departmentColumn - Column name for department (default: 'department_id')
 */
export function applyScopeFilter<T>(
  query: T,
  scope: DataScope,
  userColumn: string = 'assigned_to',
  departmentColumn: string = 'department_id',
): T {
  const q = query as any;

  switch (scope.type) {
    case 'self':
      return q.eq(userColumn, scope.user_id!);

    case 'team':
      if (scope.team_user_ids && scope.team_user_ids.length > 0) {
        return q.in(userColumn, scope.team_user_ids);
      }
      return q.eq(userColumn, scope.user_id!);

    case 'department':
      if (scope.department_id) {
        return q.eq(departmentColumn, scope.department_id);
      }
      // Fallback: if no department_id set, show own
      return q.eq(userColumn, scope.user_id!);

    case 'organization':
      // No additional filter — sees everything within org
      return q;

    default:
      return q.eq(userColumn, scope.user_id!);
  }
}

// ─── Scope label for UI ─────────────────────────────────────

export const SCOPE_LABELS: Record<ScopeType, string> = {
  self: 'Minhas tarefas',
  team: 'Minha equipe',
  department: 'Meu departamento',
  organization: 'Organização',
};

export const ROLE_TYPE_LABELS: Record<RoleType, string> = {
  operational: 'Analista',
  lead: 'Coordenador',
  manager: 'Gerente',
  director: 'Diretor',
};

// ─── Scope User IDs (DB-powered or fallback) ────────────────

/**
 * Returns all user IDs visible to the given user based on hierarchy.
 * Tries PostgreSQL RPC `get_user_scope` first; falls back to client-side resolution.
 * Results should be cached (useDataScope handles this via React Query).
 */
export async function getUserScopeIds(
  userId: string,
  organizationId: string,
): Promise<string[]> {
  // Try DB function first (most accurate — includes recursive subordinates)
  try {
    const { data, error } = await supabase.rpc('get_user_scope', { p_user_id: userId });
    if (!error && data && data.length > 0) {
      return data.map((row: { user_id: string }) => row.user_id);
    }
  } catch {
    // RPC not available — fall through to client-side
  }

  // Client-side fallback
  const scope = await getUserScope(userId, organizationId);
  switch (scope.type) {
    case 'self':
      return [userId];
    case 'team':
      return scope.team_user_ids ?? [userId];
    case 'department': {
      const { data } = await supabase
        .from('user_organizations')
        .select('user_id')
        .eq('organization_id', organizationId)
        .eq('department_id', scope.department_id!);
      return data?.map(r => r.user_id) ?? [userId];
    }
    case 'organization': {
      const { data } = await supabase
        .from('user_organizations')
        .select('user_id')
        .eq('organization_id', organizationId);
      return data?.map(r => r.user_id) ?? [userId];
    }
    default:
      return [userId];
  }
}
